import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';

import type { GraphSampleRowCounts } from './graphSampleAppIds.ts';

export type BylineSsrSample =
	| 'outboundBylineSample'
	| 'incomingLinksSample'
	| 'relatedContentSample';

export type BylineSsrRoute = 'view' | 'live';

// `SSRMeasures` renames the `ssr-app` prefix to `bifrost-ssr` before shipping the measures to the
// page as `window.__SSR_MEASURES__`, which is where `getBylineSsrSampleAttributes` reads them back.
const getMeasurePrefix = (sample: BylineSsrSample, route: BylineSsrRoute): string =>
	`ssr-app/prepare/${sample}/${route}`;

export const recordBylineSsrSampleOutcome = (
	sample: BylineSsrSample,
	route: BylineSsrRoute,
	outcome: string,
): void => {
	SSRMeasures.addMeasure(`${getMeasurePrefix(sample, route)}/outcome:${outcome}`, 0);
};

/**
 * Records how long the sample query took, including when it settles after its guard gave up, so a
 * timed-out sample still reports its real duration if it lands before the response is written.
 */
export const timeBylineSsrSample = <T>(
	sample: BylineSsrSample,
	route: BylineSsrRoute,
	samplePromise: Promise<T>,
): Promise<T> => {
	const startedAt = performance.now();
	const record = () => {
		SSRMeasures.addMeasure(
			`${getMeasurePrefix(sample, route)}/duration`,
			Math.round(performance.now() - startedAt),
			true,
		);
	};
	samplePromise.then(record, record);
	return samplePromise;
};

/**
 * Records a graph sample's row counts as zero-length markers once it settles, including after a
 * timeout, so the slowest samples are described too. CCINT-9673
 */
export const recordBylineSsrSampleRows = <
	T extends { status: string; rows?: GraphSampleRowCounts },
>(
	sample: Exclude<BylineSsrSample, 'outboundBylineSample'>,
	route: BylineSsrRoute,
	samplePromise: Promise<T>,
): Promise<T> =>
	samplePromise.then((result) => {
		if (result.rows) {
			const prefix = getMeasurePrefix(sample, route);
			SSRMeasures.addMeasure(`${prefix}/rows:${result.rows.total}`, 0);
			SSRMeasures.addMeasure(`${prefix}/thirdPartyRows:${result.rows.thirdParty}`, 0);
			SSRMeasures.addMeasure(`${prefix}/unhydratedRows:${result.rows.unhydrated}`, 0);
		}
		return result;
	});
